import { Fragment, createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { setRequestPreview } from "@/lib/preview-request.mjs";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [realUser, setRealUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreviewState] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [organizationError, setOrganizationError] = useState("");
  const authGeneration = useRef(0);
  const setPreview = useCallback((role, organizationId = "") => {
    const next = realUser?.role === "admin" && ["user", "manager"].includes(role)
      ? { role, organization_id: organizations.some(org => org.id === organizationId) ? organizationId : null } : null;
    setRequestPreview(next); setPreviewState(next);
  }, [realUser?.role, organizations]);
  const fetchMe = useCallback(async () => {
    const generation = ++authGeneration.current;
    try {
      const { data } = await api.get("/auth/me", { previewControl: true });
      if (generation !== authGeneration.current) return;
      setRealUser(data);
      if (data.role !== "admin") { setRequestPreview(null); setPreviewState(null); setOrganizations([]); }
    } catch {
      if (generation !== authGeneration.current) return;
      setRealUser(false); setRequestPreview(null); setPreviewState(null); setOrganizations([]);
    } finally { if (generation === authGeneration.current) setLoading(false); }
  }, []);
  useEffect(() => {
    setRequestPreview(null); fetchMe();
    return () => { authGeneration.current += 1; setRequestPreview(null); };
  }, [fetchMe]);
  useEffect(() => {
    let active = true;
    if (realUser?.role === "admin") {
      api.get("/auth/preview-organizations", { previewControl: true }).then(({ data }) => {
        if (active) { setOrganizations(data); setOrganizationError(""); }
      }).catch(() => { if (active) { setOrganizations([]); setOrganizationError("Organisaties konden niet worden geladen."); } });
    }
    return () => { active = false; };
  }, [realUser?.id, realUser?.role]);
  const login = async (email, password) => {
    setRequestPreview(null); setPreviewState(null);
    const { data } = await api.post("/auth/login", { email, password });
    await fetchMe(); return data.user;
  };
  const logout = async () => {
    authGeneration.current += 1;
    setRequestPreview(null); setPreviewState(null); setRealUser(false); setOrganizations([]);
    try { await api.post("/auth/logout"); } catch {}
  };
  const user = preview && realUser?.role === "admin" ? { ...realUser, role: preview.role,
    organization_id: preview.organization_id, organization: organizations.find(org => org.id === preview.organization_id) || null,
    all_devices_visible: organizations.find(org => org.id === preview.organization_id)?.all_devices_visible === true,
    preview_read_only: true } : realUser;
  const scope = [realUser?.id || "guest", preview?.role || "self", preview?.organization_id || "none", user?.all_devices_visible === true ? "all-read" : "scoped"].join(":");
  return <AuthContext.Provider value={{ user, realUser, loading, login, logout, refresh: fetchMe,
    preview, setPreview, organizations, organizationError }}><Fragment key={scope}>{children}</Fragment></AuthContext.Provider>;
}
